import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  Pencil, Save, Plus, Upload, CheckCircle2, XCircle, FileSpreadsheet,
  Download, Trash2, FileText, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import adminService from '../../services/adminService';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const EMPTY_LICENSE = {
  licenseNumber: '', licenseState: '', licenseType: '', licenseDesignation: '',
  licenseStatus: '', licenseCategory: '', licenseVertical: '', licenseActivity: '',
  licenseTerm: '', licenseIssuedDate: '', licenseExpirationDate: '',
};

const EMPTY_CONTACT = {
  contactType: '', contactFirstName: '', contactLastName: '',
  contactAddress: '', contactPhone: '', contactEmail: '',
};

function newOperator() {
  return {
    businessName: '', dbaName: '', phone: '', email: '', website: '',
    streetAddress: '', city: '', county: '', zipCode: '',
    latitude: '', longitude: '',
    facebook: '', instagram: '', twitter: '', linkedin: '',
    energySupplier: '', certificateBody: '', notes: '',
    licenses: [{ ...EMPTY_LICENSE }],
    contacts: [{ ...EMPTY_CONTACT }],
  };
}

// ── Reusable sub-form components ──────────────────────────────────────────────

function LicenseItem({ license, index, onChange, onRemove, canRemove }) {
  const set = (field, value) => onChange(index, { ...license, [field]: value });
  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase">License {index + 1}</p>
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)}
            className="text-destructive hover:text-destructive h-6 w-6 p-0">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">License Number</Label>
          <Input placeholder="LIC-000000" value={license.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">State</Label>
          <Select value={license.licenseState || 'none'} onValueChange={v => set('licenseState', v === 'none' ? '' : v)}>
            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— None —</SelectItem>
              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">License Type</Label>
          <Input placeholder="e.g. Cultivation" value={license.licenseType} onChange={e => set('licenseType', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Designation</Label>
          <Input placeholder="e.g. Tier 1" value={license.licenseDesignation} onChange={e => set('licenseDesignation', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Input placeholder="e.g. Active" value={license.licenseStatus} onChange={e => set('licenseStatus', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Input placeholder="e.g. Medical" value={license.licenseCategory} onChange={e => set('licenseCategory', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Vertical</Label>
          <Input placeholder="e.g. Cannabis" value={license.licenseVertical} onChange={e => set('licenseVertical', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Activity</Label>
          <Input placeholder="e.g. Grow" value={license.licenseActivity} onChange={e => set('licenseActivity', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Term</Label>
          <Input placeholder="e.g. 1 Year" value={license.licenseTerm} onChange={e => set('licenseTerm', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Issued Date</Label>
          <Input type="date" value={license.licenseIssuedDate} onChange={e => set('licenseIssuedDate', e.target.value)} />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Expiration Date</Label>
          <Input type="date" value={license.licenseExpirationDate} onChange={e => set('licenseExpirationDate', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

function ContactItem({ contact, index, onChange, onRemove, canRemove }) {
  const set = (field, value) => onChange(index, { ...contact, [field]: value });
  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Contact {index + 1}</p>
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)}
            className="text-destructive hover:text-destructive h-6 w-6 p-0">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Contact Type</Label>
          <Input placeholder="e.g. Owner" value={contact.contactType} onChange={e => set('contactType', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">First Name</Label>
          <Input placeholder="First name" value={contact.contactFirstName} onChange={e => set('contactFirstName', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Last Name</Label>
          <Input placeholder="Last name" value={contact.contactLastName} onChange={e => set('contactLastName', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Phone</Label>
          <Input placeholder="(555) 000-0000" value={contact.contactPhone} onChange={e => set('contactPhone', e.target.value)} />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Email</Label>
          <Input type="email" placeholder="email@business.com" value={contact.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Address</Label>
          <Input placeholder="Address" value={contact.contactAddress} onChange={e => set('contactAddress', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// ── Operator form fields (shared between Add and Edit) ────────────────────────

function OperatorFields({ form, onChange }) {
  const set = (field, value) => onChange(p => ({ ...p, [field]: value }));
  const field = (key, label, span, type) => (
    <div key={key} className={`space-y-1 ${span || ''}`}>
      <Label>{label}</Label>
      <Input type={type || 'text'} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} />
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1 col-span-2">
        <Label>Business Name *</Label>
        <Input placeholder="Legal business name" value={form.businessName} onChange={e => set('businessName', e.target.value)} />
      </div>
      {field('dbaName', 'DBA Name')}
      {field('phone', 'Phone')}
      {field('email', 'Email', '', 'email')}
      {field('website', 'Website')}
      {field('streetAddress', 'Street Address', 'col-span-2')}
      {field('city', 'City')}
      {field('county', 'County')}
      {field('zipCode', 'Zip Code')}
      {field('latitude', 'Latitude')}
      {field('longitude', 'Longitude')}
      {field('energySupplier', 'Energy Supplier')}
      {field('certificateBody', 'Certificate Body')}
      {field('facebook', 'Facebook')}
      {field('instagram', 'Instagram')}
      {field('twitter', 'Twitter / X')}
      {field('linkedin', 'LinkedIn')}
      {field('notes', 'Notes', 'col-span-2')}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminOperatorsPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);

  // Edit dialog
  const [editOp, setEditOp] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add dialog
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(newOperator);
  const [isAdding, setIsAdding] = useState(false);

  // Bulk upload
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // ── Template download ───────────────────────────────────────────────────────

  const downloadTemplate = () => {
    const headers = [
      'BusinessName','DbaName','Phone','Email','Website',
      'StreetAddress','City','County','ZipCode','Latitude','Longitude',
      'Facebook','Instagram','Twitter','Linkedin','EnergySupplier','CertificateBody','Notes',
      'License1_Number','License1_State','License1_Type','License1_Designation',
      'License1_Status','License1_Category','License1_Vertical','License1_Activity',
      'License1_Term','License1_IssuedDate','License1_ExpirationDate',
      'License2_Number','License2_State','License2_Type','License2_Designation',
      'License2_Status','License2_Category','License2_Vertical','License2_Activity',
      'License2_Term','License2_IssuedDate','License2_ExpirationDate',
      'Contact1_Type','Contact1_FirstName','Contact1_LastName',
      'Contact1_Address','Contact1_Phone','Contact1_Email',
      'Contact2_Type','Contact2_FirstName','Contact2_LastName',
      'Contact2_Address','Contact2_Phone','Contact2_Email',
    ];
    const example = [
      'Green Leaf Cannabis Co.','Green Leaf','(555) 123-4567','contact@greenleaf.com','https://greenleaf.com',
      '123 Main St','Denver','Denver County','80203','39.7392','-104.9903',
      '','','','','Xcel Energy','','Example operator',
      'LIC-20240001','CO','Cultivation','Tier 1','Active','Medical','Cannabis','Grow','1 Year','2024-01-15','2025-01-14',
      'LIC-20240002','WA','Retail','','Active','Adult Use','Cannabis','Retail','1 Year','2024-03-01','2025-02-28',
      'Owner','Jane','Smith','123 Main St Denver CO','(555) 987-6543','jane@greenleaf.com',
      'Manager','Bob','Jones','456 Oak Ave Denver CO','(555) 111-2222','bob@greenleaf.com',
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operators');
    XLSX.writeFile(wb, 'operators_template.xlsx');
    toast.success('Template downloaded');
  };

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchOperators = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = { page, pageSize: 25 };
      if (search) filter.search = search;
      if (stateFilter) filter.state = stateFilter;
      const result = await adminService.getOperators(filter);
      setData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load operators');
    } finally {
      setIsLoading(false);
    }
  }, [search, stateFilter, page]);

  useEffect(() => { fetchOperators(); }, [fetchOperators]);

  // ── Edit ────────────────────────────────────────────────────────────────────

  const openEdit = async (op) => {
    setIsEditOpen(true);
    setIsLoadingEdit(true);
    try {
      const full = await adminService.getOperator(op.operatorId);
      const toLic = l => ({
        licenseNumber: l.licenseNumber ?? '',
        licenseState: l.state ?? '',
        licenseType: l.licenseType ?? '',
        licenseDesignation: l.licenseDesignation ?? '',
        licenseStatus: l.licenseStatus ?? '',
        licenseCategory: l.category ?? '',
        licenseVertical: l.vertical ?? '',
        licenseActivity: l.activity ?? '',
        licenseTerm: l.licenseTerm ?? '',
        licenseIssuedDate: l.issueDate ? l.issueDate.split('T')[0] : '',
        licenseExpirationDate: l.expirationDate ? l.expirationDate.split('T')[0] : '',
      });
      const toCon = c => ({
        contactType: c.contactType ?? '',
        contactFirstName: c.firstName ?? '',
        contactLastName: c.lastName ?? '',
        contactAddress: c.address ?? '',
        contactPhone: c.phone ?? '',
        contactEmail: c.email ?? '',
      });
      setEditOp({
        operatorId: full.operatorId,
        businessName: full.businessName ?? '',
        dbaName: full.dbaName ?? '',
        phone: full.phone ?? '',
        email: full.email ?? '',
        website: full.website ?? '',
        streetAddress: full.streetAddress ?? '',
        city: full.city ?? '',
        county: full.county ?? '',
        zipCode: full.zipCode ?? '',
        latitude: full.latitude ?? '',
        longitude: full.longitude ?? '',
        facebook: full.facebook ?? '',
        instagram: full.instagram ?? '',
        twitter: full.twitter ?? '',
        linkedin: full.linkedin ?? '',
        energySupplier: full.energySupplier ?? '',
        certificateBody: full.certificateBody ?? '',
        notes: full.notes ?? '',
        licenses: full.licenses?.length > 0 ? full.licenses.map(toLic) : [{ ...EMPTY_LICENSE }],
        contacts: full.contacts?.length > 0 ? full.contacts.map(toCon) : [{ ...EMPTY_CONTACT }],
      });
    } catch {
      toast.error('Failed to load operator details');
      setIsEditOpen(false);
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const handleSave = async () => {
    if (!editOp) return;
    setIsSaving(true);
    try {
      const { operatorId, licenses, contacts, ...rest } = editOp;
      await adminService.updateOperator(operatorId, { ...rest, licenses, contacts });
      toast.success('Operator updated');
      setIsEditOpen(false);
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update operator');
    } finally {
      setIsSaving(false);
    }
  };

  // Edit licenses/contacts array helpers
  const upEditLic = (i, v) => setEditOp(p => ({ ...p, licenses: p.licenses.map((l, j) => j === i ? v : l) }));
  const rmEditLic = (i)    => setEditOp(p => ({ ...p, licenses: p.licenses.filter((_, j) => j !== i) }));
  const addEditLic = ()    => setEditOp(p => ({ ...p, licenses: [...p.licenses, { ...EMPTY_LICENSE }] }));
  const upEditCon = (i, v) => setEditOp(p => ({ ...p, contacts: p.contacts.map((c, j) => j === i ? v : c) }));
  const rmEditCon = (i)    => setEditOp(p => ({ ...p, contacts: p.contacts.filter((_, j) => j !== i) }));
  const addEditCon = ()    => setEditOp(p => ({ ...p, contacts: [...p.contacts, { ...EMPTY_CONTACT }] }));

  // ── Add ─────────────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    if (!addForm.businessName.trim()) { toast.error('Business Name is required'); return; }
    setIsAdding(true);
    try {
      await adminService.createOperator(addForm);
      toast.success('Operator created');
      setIsAddOpen(false);
      setAddForm(newOperator());
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create operator');
    } finally {
      setIsAdding(false);
    }
  };

  // Add licenses/contacts array helpers
  const upAddLic = (i, v) => setAddForm(p => ({ ...p, licenses: p.licenses.map((l, j) => j === i ? v : l) }));
  const rmAddLic = (i)    => setAddForm(p => ({ ...p, licenses: p.licenses.filter((_, j) => j !== i) }));
  const addAddLic = ()    => setAddForm(p => ({ ...p, licenses: [...p.licenses, { ...EMPTY_LICENSE }] }));
  const upAddCon = (i, v) => setAddForm(p => ({ ...p, contacts: p.contacts.map((c, j) => j === i ? v : c) }));
  const rmAddCon = (i)    => setAddForm(p => ({ ...p, contacts: p.contacts.filter((_, j) => j !== i) }));
  const addAddCon = ()    => setAddForm(p => ({ ...p, contacts: [...p.contacts, { ...EMPTY_CONTACT }] }));

  // ── Bulk upload ─────────────────────────────────────────────────────────────

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setIsUploading(true);
    setBulkResult(null);
    try {
      const result = await adminService.bulkUploadOperators(file);
      setBulkResult(result);
      toast.success(`Bulk upload complete: ${result.created} created`);
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Bulk upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Operators (Admin)</h1>
            <p className="text-muted-foreground mt-1">Manage all operators — add individually or bulk upload from Excel</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={fetchOperators}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />Download Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Bulk Upload Excel
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} />
            <Button size="sm" onClick={() => { setAddForm(newOperator()); setIsAddOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Add Operator
            </Button>
          </div>
        </div>

        {/* Format hint */}
        <Card className="border-dashed">
          <CardContent className="p-4 flex items-start gap-3">
            <FileSpreadsheet className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground space-y-1">
              <p><span className="font-medium text-foreground">Operator: </span>BusinessName*, DbaName, Phone, Email, Website, StreetAddress, City, County, ZipCode, Latitude, Longitude, ...</p>
              <p><span className="font-medium text-foreground">Licenses: </span>License1_Number, License1_State, License1_Type, ... License2_Number, License2_State, ... (numbered columns — add more as needed)</p>
              <p><span className="font-medium text-foreground">Contacts: </span>Contact1_Type, Contact1_FirstName, Contact1_LastName, Contact1_Email, ... Contact2_Type, ... (numbered columns)</p>
            </div>
          </CardContent>
        </Card>

        {/* Bulk result */}
        {bulkResult && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="h-5 w-5" /><span className="font-semibold">{bulkResult.created} created</span>
                </div>
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" /><span className="font-semibold">{bulkResult.failed} failed</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setBulkResult(null)}>Dismiss</Button>
              </div>
              {bulkResult.errors?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {bulkResult.errors.map((err, i) => <p key={i} className="text-xs text-red-600">• {err}</p>)}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={e => { e.preventDefault(); setPage(1); fetchOperators(); }} className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by business name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={stateFilter || 'all'} onValueChange={v => { setStateFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-36"><SelectValue placeholder="All States" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Operators</CardTitle>
            <CardDescription>{data.total} total operators</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business Name</TableHead>
                        <TableHead>DBA</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Licenses</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items?.map(op => (
                        <TableRow key={op.operatorId}>
                          <TableCell className="font-medium">{op.businessName}</TableCell>
                          <TableCell className="text-muted-foreground">{op.dbaName || '—'}</TableCell>
                          <TableCell>{op.city || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{op.phone || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{op.email || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{op.licenses?.length ?? 0} license{op.licenses?.length !== 1 ? 's' : ''}</Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(op)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Edit Dialog ──────────────────────────────────────────────────────── */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Operator</DialogTitle>
            <DialogDescription>Update operator details, licenses, and contacts</DialogDescription>
          </DialogHeader>
          {isLoadingEdit ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : editOp && (
            <div className="space-y-6">
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">Operator Information</p>
                <OperatorFields form={editOp} onChange={setEditOp} />
              </div>

              {/* Licenses */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" />Licenses ({editOp.licenses.length})
                  </p>
                  <Button variant="outline" size="sm" onClick={addEditLic}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add License
                  </Button>
                </div>
                <div className="space-y-3">
                  {editOp.licenses.map((lic, i) => (
                    <LicenseItem key={i} index={i} license={lic}
                      onChange={upEditLic} onRemove={rmEditLic}
                      canRemove={editOp.licenses.length > 1} />
                  ))}
                </div>
              </div>

              {/* Contacts */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />Contacts ({editOp.contacts.length})
                  </p>
                  <Button variant="outline" size="sm" onClick={addEditCon}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add Contact
                  </Button>
                </div>
                <div className="space-y-3">
                  {editOp.contacts.map((con, i) => (
                    <ContactItem key={i} index={i} contact={con}
                      onChange={upEditCon} onRemove={rmEditCon}
                      canRemove={editOp.contacts.length > 1} />
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add Operator Dialog ───────────────────────────────────────────────── */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Operator</DialogTitle>
            <DialogDescription>Business Name is required. Add as many licenses and contacts as needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">Operator Information</p>
              <OperatorFields form={addForm} onChange={setAddForm} />
            </div>

            {/* Licenses */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />Licenses ({addForm.licenses.length})
                </p>
                <Button variant="outline" size="sm" onClick={addAddLic}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add License
                </Button>
              </div>
              <div className="space-y-3">
                {addForm.licenses.map((lic, i) => (
                  <LicenseItem key={i} index={i} license={lic}
                    onChange={upAddLic} onRemove={rmAddLic}
                    canRemove={addForm.licenses.length > 1} />
                ))}
              </div>
            </div>

            {/* Contacts */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />Contacts ({addForm.contacts.length})
                </p>
                <Button variant="outline" size="sm" onClick={addAddCon}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add Contact
                </Button>
              </div>
              <div className="space-y-3">
                {addForm.contacts.map((con, i) => (
                  <ContactItem key={i} index={i} contact={con}
                    onChange={upAddCon} onRemove={rmAddCon}
                    canRemove={addForm.contacts.length > 1} />
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleAdd} disabled={isAdding}>
                {isAdding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Operator
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
